import { useCallback, useMemo } from 'react'

import { DoneAllOutlined, ErrorOutlineOutlined, HourglassTop, Mail } from '@mui/icons-material'
import { Box, Button, Paper, Stack, Tooltip, Typography } from '@mui/material'

import { preview } from '@/components/form/preview'

import { EmailBody } from './EmailBody'
import { SentBy } from './SentBy'
import { Status } from './Status'

import { useTexts } from '../useTexts'

interface Props {
  item: uui.domain.client.rm.OrderStepEmailNotification
  showModal: (data: uui.domain.client.rm.OrderStepEmailNotification) => void
}

export function EmailNotificationItem(props: Props) {
  const { item, showModal } = props

  const texts = useTexts()

  const handleOnShowModal = useCallback(() => {
    showModal(item)
  }, [item, showModal])

  const status = useMemo(() => {
    if (item.status === 'FAILED')
      return (
        <Tooltip title={texts.failedStatus} placement="bottom">
          <ErrorOutlineOutlined color="error" fontSize="small" />
        </Tooltip>
      )

    if (item.status === 'PENDING')
      return (
        <Tooltip title={texts.pendingStatus} placement="bottom">
          <HourglassTop color="action" fontSize="small" />
        </Tooltip>
      )
    return (
      <Tooltip title={texts.sentStatus} placement="bottom">
        <DoneAllOutlined color="success" fontSize="small" />
      </Tooltip>
    )
  }, [item.status, texts])

  return (
    <preview.Section>
      <Paper elevation={0} variant="outlined" sx={{ padding: 2 }}>
        <Stack direction="column" spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body1">
              <Box display="flex" alignItems="center">
                <Mail sx={{ mr: 1 }} />
                {texts.email}
              </Box>
            </Typography>

            <Status sentTs={item.sentTs} status={status} />
          </Stack>

          <Stack direction="column" spacing={1} alignItems="flex-start">
            <EmailBody body={item.bodyPlainText} subject={item.subject} />

            <Button
              data-testid="orderform_order-notification-tab-view-button"
              data-trackid="orderform_order-notification-tab-view-button"
              onClick={handleOnShowModal}
              size="small"
            >
              {texts.viewSettings}
            </Button>

            <SentBy recipient={item.recipient} sender={texts.senderText} />
          </Stack>
        </Stack>
      </Paper>
    </preview.Section>
  )
}
